/*
 * HIIVE Offers — central engine for the four promotional offers.
 *
 * Offer config (qualifying variants, gift pool, bundle components, quantities)
 * lives in Shopify metaobjects/metafields and is rendered server-side by
 * sections/hiive-offer-data.liquid as a JSON island. This file only reacts to
 * that data and to cart state — no campaign specifics are hardcoded here.
 *
 * Public API: window.HiiveOffers.syncCart()
 */
(function () {
  if (window.HiiveOffers) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var PARAM_KEY = 'offer';
  var CART_ATTR_KEY = '_hiive_active_offer';
  var SESSION_KEY = 'hiive_active_offer';
  var CONFIG_CACHE_PREFIX = 'hiive_offer_config_';
  var DATA_ISLAND_ID = 'HiiveOfferData';

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* Verbose diagnostics. Enable by visiting any page with ?hiive_debug=1
     (persists for the session) — then watch the console. */
  function hlog() {
    if (!HiiveOffers || !HiiveOffers.debugEnabled) return;
    var args = ['[HiiveOffers]'].concat([].slice.call(arguments));
    console.info.apply(console, args);
  }

  function jsonFetch(path, options) {
    return fetch(ROOT.replace(/\/$/, '') + path, options).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          return Promise.reject(Object.assign({ status: res.status }, body));
        });
      }
      return res.json();
    });
  }

  function postJSON(path, body) {
    /* Count our own in-flight cart writes so the global fetch hook can tell
       them apart from adds made by the theme/apps and not re-trigger sync. */
    HiiveOffers._ownRequests++;
    function settle() {
      HiiveOffers._ownRequests = Math.max(0, HiiveOffers._ownRequests - 1);
    }
    return jsonFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(
      function (res) { settle(); return res; },
      function (err) { settle(); return Promise.reject(err); }
    );
  }

  function getCart() {
    return jsonFetch('/cart.js');
  }

  function addItems(items) {
    return postJSON('/cart/add.js', { items: items }).then(function (res) {
      HiiveOffers._dirty = true;
      return res;
    });
  }

  function changeLine(key, quantity) {
    return postJSON('/cart/change.js', { id: key, quantity: quantity }).then(function (res) {
      HiiveOffers._dirty = true;
      return res;
    });
  }

  function updateCartAttributes(attributes) {
    return postJSON('/cart/update.js', { attributes: attributes });
  }

  /* ---- offer config: read the current page's data island, or fall back
     to whatever was cached the last time this offer's data island was seen
     (needed on pages like /cart that never render one). ---- */

  function readDataIsland(doc) {
    var el = (doc || document).getElementById(DATA_ISLAND_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function cacheOfferConfig(config) {
    if (!config || !config.offer_id) return;
    try {
      sessionStorage.setItem(CONFIG_CACHE_PREFIX + config.offer_id, JSON.stringify(config));
    } catch (e) {
      /* storage unavailable, ignore */
    }
  }

  function getCachedOfferConfig(offerId) {
    try {
      var raw = sessionStorage.getItem(CONFIG_CACHE_PREFIX + offerId);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  /* Re-render the offer's data section via the Section Rendering API for
     fresh inventory. JSON-template sections must be requested by their
     dynamic id (config.section_id, e.g. "template--123__hiive-offer-data"),
     against a page URL that actually renders them (config._source_path). */
  function refreshDataIsland(config) {
    if (!config || !config.section_id) return Promise.resolve(null);
    var path = config._source_path || window.location.pathname;
    var url = path + '?section_id=' + encodeURIComponent(config.section_id);
    return fetch(url)
      .then(function (res) { return res.ok ? res.text() : null; })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return readDataIsland(doc);
      })
      .catch(function () { return null; });
  }

  /* ---- highest-available-inventory gift selection ---- */

  function pickHighestInventoryGift(config, excludeVariantIds) {
    return refreshDataIsland(config).then(function (fresh) {
      var pool = (fresh && fresh.gift_pool) || config.gift_pool || [];
      var candidates = pool.filter(function (v) {
        return excludeVariantIds.indexOf(v.id) === -1 &&
          v.available &&
          (v.inventory_policy === 'continue' || v.inventory_quantity > 0);
      });
      candidates.sort(function (a, b) { return b.inventory_quantity - a.inventory_quantity; });
      return candidates.map(function (v) { return v.id; });
    });
  }

  function addGiftWithFallback(config, quantity) {
    var tried = [];
    function attempt(candidateIds) {
      if (!candidateIds.length) {
        HiiveOffers.showMessage(config.unavailable_message || 'The complimentary gift is currently unavailable.');
        return Promise.resolve(null);
      }
      var variantId = candidateIds[0];
      tried.push(variantId);
      hlog('gift: attempting variant', variantId);
      return addItems([{
        id: variantId,
        quantity: quantity,
        properties: {
          _hiive_auto_item: 'true',
          _hiive_offer_id: config.offer_id,
          _hiive_offer_role: 'shake_gift'
        }
      }]).then(function () {
        hlog('gift: added variant', variantId);
        return variantId;
      }).catch(function (err) {
        console.error('[HiiveOffers] gift add rejected for variant ' + variantId, err);
        return attempt(candidateIds.slice(1));
      });
    }
    return pickHighestInventoryGift(config, tried).then(attempt);
  }

  /* ---- reconciliation: add missing automatic items, remove orphaned ones ---- */

  function findPaidQualifyingItem(cart, qualifyingVariantIds) {
    return cart.items.filter(function (item) {
      return qualifyingVariantIds.indexOf(item.variant_id) !== -1 &&
        !(item.properties && item.properties._hiive_auto_item);
    })[0] || null;
  }

  function findAutoItem(cart, offerId, role) {
    return cart.items.filter(function (item) {
      return item.properties &&
        item.properties._hiive_auto_item === 'true' &&
        item.properties._hiive_offer_id === offerId &&
        item.properties._hiive_offer_role === role;
    })[0] || null;
  }

  function ensureAutomaticItems(cart, config) {
    if (config.offer_type === 'bundle_fixed') return Promise.resolve();

    var paidItem = findPaidQualifyingItem(cart, config.qualifying_variant_ids);
    hlog('ensure: qualifying paid item =', paidItem ? paidItem.variant_id : 'NONE');
    if (!paidItem) return Promise.resolve();

    var toAdd = [];
    var work = Promise.resolve();

    if (config.same_variant_gift && !findAutoItem(cart, config.offer_id, 'same_product_bogo')) {
      toAdd.push({
        id: paidItem.variant_id,
        quantity: config.same_variant_gift_quantity || 1,
        properties: {
          _hiive_auto_item: 'true',
          _hiive_offer_id: config.offer_id,
          _hiive_offer_role: 'same_product_bogo'
        }
      });
    }

    if (toAdd.length) {
      hlog('ensure: adding same-variant reward', toAdd[0].id);
      work = work.then(function () {
        return addItems(toAdd).catch(function (err) {
          console.error('[HiiveOffers] same-variant reward add failed', err);
        });
      });
    }

    if (config.inventory_gift_enabled && !findAutoItem(cart, config.offer_id, 'shake_gift')) {
      work = work.then(function () {
        hlog('ensure: selecting inventory gift');
        return addGiftWithFallback(config, config.inventory_gift_quantity || 1);
      });
    }

    return work;
  }

  function lookupConfig(offerId, currentConfig) {
    if (currentConfig && currentConfig.offer_id === offerId) return currentConfig;
    return getCachedOfferConfig(offerId);
  }

  /* Remove reward items whose qualifying paid item is gone.
     Returns the set of removed line keys so later steps can skip them. */
  function removeOrphanedAutoItems(cart, currentConfig) {
    var autoItems = cart.items.filter(function (item) {
      return item.properties && item.properties._hiive_auto_item === 'true';
    });

    var removedKeys = {};
    var work = Promise.resolve();
    autoItems.forEach(function (item) {
      if (item.properties._hiive_offer_role === 'bundle_component') return;

      var config = lookupConfig(item.properties._hiive_offer_id, currentConfig);
      if (!config) return; // config unknown this session — leave the line alone

      var stillQualifies = findPaidQualifyingItem(cart, config.qualifying_variant_ids);
      if (!stillQualifies) {
        removedKeys[item.key] = true;
        work = work.then(function () { return changeLine(item.key, 0); });
      }
    });
    return work.then(function () { return removedKeys; });
  }

  /* Enforce reward quantities (one per offer per order) and collapse any
     duplicate reward lines that slipped through a race between syncs. */
  function normalizeAutoItems(cart, removedKeys, currentConfig) {
    var seen = {};
    var work = Promise.resolve();
    cart.items.forEach(function (item) {
      if (!(item.properties && item.properties._hiive_auto_item === 'true')) return;
      if (removedKeys && removedKeys[item.key]) return;

      var role = item.properties._hiive_offer_role;
      var offerId = item.properties._hiive_offer_id;
      var config = lookupConfig(offerId, currentConfig);

      if (role !== 'bundle_component') {
        var dedupeKey = offerId + ':' + role;
        if (seen[dedupeKey]) {
          work = work.then(function () { return changeLine(item.key, 0); });
          return;
        }
        seen[dedupeKey] = true;
      }

      var maxQty = 1;
      if (config && role === 'shake_gift') maxQty = config.inventory_gift_quantity || 1;
      if (config && role === 'same_product_bogo') maxQty = config.same_variant_gift_quantity || 1;
      if (item.quantity > maxQty) {
        work = work.then(function () { return changeLine(item.key, maxQty); });
      }
    });
    return work;
  }

  /* ---- public engine ---- */

  var HiiveOffers = {
    version: '1.5.0',
    debugEnabled: false,
    _locked: false,
    _pendingSync: false,
    _dirty: false,
    _selfRender: false,
    _ownRequests: 0,

    init: function () {
      this.detectDebug();
      hlog('init v' + this.version);
      this.injectStyles();
      this.captureCampaignMarker();
      this.cacheCurrentPageOfferConfig();
      this.hookRenderContents();
      this.hookPubSub();
      this.hookFetch();
      this.syncCart();
    },

    detectDebug: function () {
      try {
        if (/[?&]hiive_debug/.test(window.location.search)) {
          sessionStorage.setItem('hiive_debug', '1');
        }
        this.debugEnabled = sessionStorage.getItem('hiive_debug') === '1';
      } catch (e) {
        this.debugEnabled = false;
      }
    },

    /* One-line console status report: window.HiiveOffers.debug() */
    debug: function () {
      var self = this;
      return getCart().then(function (cart) {
        var island = readDataIsland();
        var info = {
          version: self.version,
          activeOfferId: self.getActiveOfferId(cart),
          islandPresent: !!island,
          islandOfferId: island ? island.offer_id : null,
          islandContext: island ? island.context : null,
          cartAttributes: cart.attributes,
          items: cart.items.map(function (i) {
            return { variant_id: i.variant_id, qty: i.quantity, props: i.properties };
          })
        };
        console.log('[HiiveOffers] status', info);
        return info;
      });
    },

    injectStyles: function () {
      if (document.getElementById('HiiveOffersStyles')) return;
      var style = document.createElement('style');
      style.id = 'HiiveOffersStyles';
      style.textContent =
        '.hiive-offer-badge{display:inline-block;padding:.3em .8em;border-radius:999px;' +
        'font-size:1.1rem;font-weight:700;letter-spacing:.02em;text-transform:uppercase;}' +
        '.hiive-offer-badge--gift{background:#1a7a3a;color:#fff;}' +
        '.hiive-offer-badge--bundle{background:#2a4d8f;color:#fff;}' +
        '.hiive-offer-badge--savings{background:#d4911e;color:#111;}' +
        '.hiive-cart-item__qty-static{font-size:1.2rem;opacity:.75;margin:.4em 0 0;}';
      document.head.appendChild(style);
    },

    captureCampaignMarker: function () {
      var params = new URLSearchParams(window.location.search);
      var offerParam = params.get(PARAM_KEY);
      var islandConfig = readDataIsland();

      /* Entering the offer flow = an explicit ?offer= param, OR landing on
         an offer collection page (only offer landing templates render the
         data island in a collection context). A PDP island alone does NOT
         activate — direct product visits must stay non-promotional. */
      var active = offerParam ||
        (islandConfig && islandConfig.context === 'collection' && islandConfig.offer_id) ||
        null;
      if (!active) {
        hlog('no activation on this page (no ?offer param, no collection island)');
        return;
      }
      hlog('offer activated:', active);
      this.setActiveOffer(active);
    },

    setActiveOffer: function (offerId) {
      try {
        sessionStorage.setItem(SESSION_KEY, offerId);
      } catch (e) { /* ignore */ }
      var attrs = {};
      attrs[CART_ATTR_KEY] = offerId;
      updateCartAttributes(attrs).catch(function () { /* non-fatal */ });
    },

    cacheCurrentPageOfferConfig: function () {
      var config = readDataIsland();
      if (config) {
        /* Remember which page rendered this island so inventory can be
           re-fetched later from pages (like /cart) that don't render one. */
        config._source_path = window.location.pathname;
        cacheOfferConfig(config);
      }
    },

    getActiveOfferId: function (cart) {
      var id = null;
      try {
        id = sessionStorage.getItem(SESSION_KEY);
      } catch (e) { /* ignore */ }
      /* Fallback: the marker also lives on the cart itself, which survives
         new tabs and cleared session storage. */
      if (!id && cart && cart.attributes && cart.attributes[CART_ATTR_KEY]) {
        id = cart.attributes[CART_ATTR_KEY];
        try {
          sessionStorage.setItem(SESSION_KEY, id);
        } catch (e) { /* ignore */ }
      }
      return id;
    },

    hookRenderContents: function () {
      function patch(ctor) {
        if (!ctor || ctor.__hiivePatched || typeof ctor.prototype.renderContents !== 'function') return;
        var original = ctor.prototype.renderContents;
        ctor.prototype.renderContents = function (parsedState) {
          var result = original.call(this, parsedState);
          /* Don't reschedule when we triggered this render ourselves —
             that would loop: sync -> refresh -> render -> sync -> ... */
          if (!HiiveOffers._selfRender) HiiveOffers.scheduleSync();
          return result;
        };
        ctor.__hiivePatched = true;
      }
      if (window.customElements) {
        customElements.whenDefined('cart-notification').then(function () {
          patch(customElements.get('cart-notification'));
        }).catch(function () {});
        customElements.whenDefined('cart-items').then(function () {
          patch(customElements.get('cart-items'));
        }).catch(function () {});
      }
    },

    hookPubSub: function () {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          if (event && event.source === 'hiive-offers') return;
          HiiveOffers.scheduleSync();
        });
      }
    },

    /* Safety net: no matter which script performs a cart write (theme
       product form, GemPages, quick view, an app), a successful call to
       /cart/add|change|update|clear triggers a sync. Our own writes are
       excluded via the _ownRequests counter. */
    hookFetch: function () {
      if (window.__hiiveFetchHooked) return;
      window.__hiiveFetchHooked = true;
      var origFetch = window.fetch;
      window.fetch = function () {
        var arg = arguments[0];
        var url = typeof arg === 'string' ? arg : (arg && arg.url) || '';
        var result = origFetch.apply(this, arguments);
        if (/\/cart\/(add|change|update|clear)/.test(url)) {
          result.then(function (res) {
            if (res && res.ok && HiiveOffers._ownRequests === 0) {
              hlog('cart write detected via fetch:', url);
              HiiveOffers.scheduleSync();
            }
          }).catch(function () { /* observer only — never affect the caller */ });
        }
        return result;
      };
    },

    scheduleSync: debounce(function () { HiiveOffers.syncCart(); }, 400),

    syncCart: function () {
      var self = this;
      if (self._locked) {
        self._pendingSync = true;
        return Promise.resolve();
      }
      self._locked = true;

      return getCart().then(function (cart) {
        var activeOfferId = self.getActiveOfferId(cart);
        /* Prefer the live island; fall back to the cached copy so rewards
           can still be reconciled on pages without one (e.g. /cart). */
        var currentConfig = readDataIsland() || getCachedOfferConfig(activeOfferId);

        var offerActive = !!(currentConfig && activeOfferId && currentConfig.offer_id === activeOfferId);
        var hasAutoItems = cart.items.some(function (item) {
          return item.properties && item.properties._hiive_auto_item === 'true';
        });
        hlog('sync: activeOffer=' + activeOfferId,
          'config=' + (currentConfig ? currentConfig.offer_id : 'none'),
          'offerActive=' + offerActive, 'autoItemsInCart=' + hasAutoItems,
          'cartItems=' + cart.items.length);
        if (!offerActive && !hasAutoItems) return; // nothing to reconcile

        return removeOrphanedAutoItems(cart, currentConfig)
          .then(function (removedKeys) {
            return normalizeAutoItems(cart, removedKeys, currentConfig);
          })
          .then(function () {
            if (!offerActive) return;
            return getCart().then(function (freshCart) {
              return ensureAutomaticItems(freshCart, currentConfig);
            });
          });
      }).then(function () {
        /* Only re-render cart UI when this sync actually changed the cart —
           avoids redundant section fetches and any render->sync echo. */
        if (self._dirty) {
          self._dirty = false;
          return self.refreshCartUI();
        }
      }).catch(function (e) {
        console.error('[HiiveOffers] sync failed', e);
      }).then(function () {
        self._locked = false;
        if (self._pendingSync) {
          self._pendingSync = false;
          self.scheduleSync();
        }
      });
    },

    refreshCartUI: function () {
      var notification = document.querySelector('cart-notification');
      var cartItems = document.querySelector('cart-items');
      if (!notification && !cartItems) return Promise.resolve();

      var sectionIds = [];
      if (notification && typeof notification.getSectionsToRender === 'function') {
        notification.getSectionsToRender().forEach(function (s) { sectionIds.push(s.id); });
      }
      if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
        cartItems.getSectionsToRender().forEach(function (s) { sectionIds.push(s.section || s.id); });
      }
      if (!sectionIds.length) return Promise.resolve();

      return fetch(ROOT.replace(/\/$/, '') + '/?sections=' + sectionIds.join(','))
        .then(function (res) { return res.json(); })
        .then(function (sections) {
          return getCart().then(function (cart) {
            cart.sections = sections;

            /* Update the drawer's markup in place WITHOUT calling its
               renderContents(), which unconditionally calls this.open() —
               fine for a real "item just added" submit, wrong for a
               background sync on page load or an unrelated cart-page edit.
               It would otherwise pop the drawer open on every page. */
            if (notification && typeof notification.getSectionsToRender === 'function') {
              notification.getSectionsToRender().forEach(function (section) {
                var el = document.getElementById(section.id);
                if (el && sections[section.id] != null) {
                  el.innerHTML = notification.getSectionInnerHTML(sections[section.id]);
                }
              });
            }

            if (cartItems) {
              HiiveOffers._selfRender = true;
              try {
                cartItems.renderContents(cart);
              } finally {
                HiiveOffers._selfRender = false;
              }
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hiive-offers', cartData: cart });
            }
          });
        })
        .catch(function (e) { console.error('[HiiveOffers] refresh failed', e); });
    },

    /* ---- bundle add-to-cart (Offers 3 & 4) ---- */

    addBundleToCart: function (button, offerId, componentVariantIds, quantity) {
      var qty = parseInt(quantity, 10) || 1;
      if (button.disabled) return;
      button.disabled = true;
      button.classList.add('loading');

      var self = this;
      getCart().then(function (cart) {
        var alreadyAdded = cart.items.some(function (item) {
          return item.properties &&
            item.properties._hiive_offer_id === offerId &&
            item.properties._hiive_offer_role === 'bundle_component';
        });
        if (alreadyAdded) return null;

        var items = componentVariantIds.map(function (variantId) {
          return {
            id: variantId,
            quantity: qty,
            properties: {
              _hiive_auto_item: 'true',
              _hiive_offer_id: offerId,
              _hiive_offer_role: 'bundle_component'
            }
          };
        });
        return addItems(items);
      }).then(function () {
        return self.refreshCartUI();
      }).catch(function () {
        self.showMessage('One of the bundle items is currently unavailable. Please try again shortly.');
      }).then(function () {
        button.disabled = false;
        button.classList.remove('loading');
      });
    },

    /* ---- direct-checkout (cart permalink) flow ----
       Builds /cart/{id}:{qty},... which REPLACES the cart contents and
       lands the customer straight in checkout with the whole offer
       pre-loaded. Shopify automatic discounts apply at checkout as usual. */

    clearActiveOffer: function () {
      try {
        sessionStorage.removeItem(SESSION_KEY);
      } catch (e) { /* ignore */ }
      var attrs = {};
      attrs[CART_ATTR_KEY] = '';
      return updateCartAttributes(attrs).catch(function () { /* non-fatal */ });
    },

    _goToPermalink: function (parts) {
      var base = ROOT.replace(/\/$/, '');
      /* Clear the offer marker first so the background sync can never
         double-add rewards if the customer backs out of checkout. */
      this.clearActiveOffer().then(function () {
        window.location.href = base + '/cart/' + parts.join(',');
      });
    },

    /* Offers 1 & 2: paid unit (+ free same-variant units) + highest-stock gift. */
    buyNow: function (variantId) {
      var self = this;
      var config = readDataIsland() || getCachedOfferConfig(self.getActiveOfferId());
      hlog('buyNow', variantId, 'config=', config ? config.offer_id : 'none');
      if (!config) {
        self._goToPermalink([variantId + ':1']);
        return;
      }

      var qty = 1;
      if (config.same_variant_gift) qty += config.same_variant_gift_quantity || 1;
      var parts = [variantId + ':' + qty];

      if (!config.inventory_gift_enabled) {
        self._goToPermalink(parts);
        return;
      }

      refreshDataIsland(config).then(function (fresh) {
        var pool = ((fresh && fresh.gift_pool) || config.gift_pool || []).filter(function (v) {
          return v.available && (v.inventory_policy === 'continue' || v.inventory_quantity > 0);
        });
        pool.sort(function (a, b) { return b.inventory_quantity - a.inventory_quantity; });
        if (pool[0]) {
          hlog('buyNow: gift selected', pool[0].id);
          parts.push(pool[0].id + ':' + (config.inventory_gift_quantity || 1));
        } else {
          self.showMessage(config.unavailable_message || 'The complimentary gift is currently unavailable.');
        }
        self._goToPermalink(parts);
      });
    },

    /* Offers 3 & 4: all bundle components, `quantity` units each (default 1). */
    buyBundleNow: function (componentVariantIds, quantity) {
      var qty = parseInt(quantity, 10) || 1;
      var parts = componentVariantIds.map(function (id) { return id + ':' + qty; });
      hlog('buyBundleNow', parts.join(','));
      this._goToPermalink(parts);
    },

    /* ---- non-blocking error surface ---- */

    showMessage: function (text) {
      var el = document.getElementById('HiiveOffersMessage');
      if (!el) {
        el = document.createElement('div');
        el.id = 'HiiveOffersMessage';
        el.setAttribute('role', 'status');
        el.style.cssText = 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);' +
          'background:#1a1a1a;color:#fff;padding:12px 20px;border-radius:8px;z-index:9999;' +
          'font-size:14px;max-width:90vw;box-shadow:0 4px 12px rgba(0,0,0,.25);';
        document.body.appendChild(el);
      }
      el.textContent = text;
      el.style.display = 'block';
      clearTimeout(el.__hideTimer);
      el.__hideTimer = setTimeout(function () { el.style.display = 'none'; }, 5000);
    }
  };

  window.HiiveOffers = HiiveOffers;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { HiiveOffers.init(); });
  } else {
    HiiveOffers.init();
  }
})();
